import type { HipSpineApprovedLandmarkApiObject } from '@/api/hip-spine-study/hipSpineLandmark';
import {
    computeAPPAngle,
    computeLumbarLordosisAtLevel,
    computePelvicIncidence,
    hasLumbarEndplatePair,
    hasSacralEndplatePair,
    tryComputeLumbarLordosisAtLevel,
    tryComputeSacralSlope,
} from '@/domain/anatomy/hipSpineAngleMeasurements';
import { type LumbarLevel } from '@/domain/anatomy/hipSpineLandmarks';

export const FLATBACK_THRESHOLD_DEGREES = 10;
const STIFF_LL_THRESHOLD_DEGREES = 20;
const STIFF_SS_THRESHOLD_DEGREES = 10;
export const PPT_THRESHOLD = -13;
const L1: LumbarLevel = 'L1';

interface HipSpineClassificationInputs {
    standingLandmarks: HipSpineApprovedLandmarkApiObject;
    flexedSeatedLandmarks: HipSpineApprovedLandmarkApiObject | null;
    lumbarLandmarks: HipSpineApprovedLandmarkApiObject | null;
}

/**
 * Which measure drives the stiff-spine decision: the change in lumbar lordosis
 * (ΔLL) when the L1 endplate is used in both upright and seated postures, else
 * the change in sacral slope (ΔSS).
 */
export type StiffMetric = 'deltaLumbarLordosis' | 'deltaSacralSlope';

const STIFF_ABBREVIATION: Record<StiffMetric, 'ΔLL' | 'ΔSS'> = {
    deltaLumbarLordosis: 'ΔLL',
    deltaSacralSlope: 'ΔSS',
};

const STIFF_THRESHOLD_DEGREES: Record<StiffMetric, number> = {
    deltaLumbarLordosis: STIFF_LL_THRESHOLD_DEGREES,
    deltaSacralSlope: STIFF_SS_THRESHOLD_DEGREES,
};

/**
 * A successfully measured stiff-spine input: the chosen metric and its delta
 * (upright − flexed-seated). When stiffness cannot be measured there is no
 * measure at all (`null`), never a metric without a delta — mirroring the
 * backend, where a missing flexed-seated posture yields no mobility selection
 * (`mobility_method = None`) rather than a sentinel.
 */
export interface StiffMeasure {
    metric: StiffMetric;
    delta: number;
}

/**
 * Pure scalar measurements derived from approved landmarks.
 */
export interface HipSpineMeasurements {
    pelvicIncidence: number;
    /** Standing LL at the standing posture's declared endplate (drives flatback). */
    lumbarLordosisStanding: number;
    /** ΔLL or ΔSS measure; null when stiffness cannot be measured. */
    stiff: StiffMeasure | null;
    appAngle: number;
}

interface HipSpineClassificationFlags {
    flatback: boolean;
    stiff: boolean | null;
    ppt: boolean;
}

interface HipSpineClassificationBase {
    flatback: boolean;
    ppt: boolean;
    pelvicIncidenceMinusLumbarLordosis: number;
    appAngle: number;
    recommendations: readonly string[];
}

/** Stiffness was measurable: the metric fields are always present together. */
export interface HipSpineClassificationWithStiffness extends HipSpineClassificationBase {
    stiff: boolean;
    stiffDelta: number;
    stiffMetricAbbreviation: 'ΔLL' | 'ΔSS';
    stiffThresholdDegrees: number;
}

/**
 * Stiffness was not measurable (flexed-seated posture absent or incomplete).
 * No metric fields exist on this branch, so reading them without first
 * narrowing on `stiff` is a type error — backend parity with
 * `mobility_method = None`.
 */
export interface HipSpineClassificationWithoutStiffness extends HipSpineClassificationBase {
    stiff: null;
}

export type HipSpineClassification =
    | HipSpineClassificationWithStiffness
    | HipSpineClassificationWithoutStiffness;

const REC_LOWER_ANGLES = 'Consider targeting lower angles than native.';
const REC_DUAL_MOBILITY = 'Consider using Dual Mobility.';
const REC_INCREASE_ANTEVERSION =
    'Consider increasing anteversion and inclination from standard targets.';

/**
 * Map (flatback, stiff, ppt) booleans to a recommendation list. Mirrors the
 * Python GUIDELINES map keyed by class label, but resolved directly from the
 * boolean inputs so we don't need to expose an intermediate class string.
 *
 * For non-flatback patients (Class 1 family), PPT does not influence
 * recommendations — matching the Python classify() function which suppresses
 * the * variant when flatback is false.
 */
export function recommendationsFor(state: HipSpineClassificationFlags): readonly string[] {
    const { flatback, stiff, ppt } = state;
    if (!flatback) return stiff === true ? [REC_INCREASE_ANTEVERSION] : [];
    if (!ppt) return [];

    return stiff === true ? [REC_LOWER_ANGLES, REC_DUAL_MOBILITY] : [REC_LOWER_ANGLES];
}

/** Subtract two optional measurements, propagating null when either is missing. */
function subtractOrNull(upright: number | null, seated: number | null): number | null {
    return upright === null || seated === null ? null : upright - seated;
}

/**
 * ΔLL stiff measure at L1: upright − flexed-seated, or null when a required
 * landmark is missing. The upright source is chosen by
 * {@link selectStiffMeasure} (lumbar-sagittal preferred, else standing) to
 * mirror the backend tie-break.
 */
function lumbarLordosisStiffMeasure(
    upright: HipSpineApprovedLandmarkApiObject,
    seated: HipSpineApprovedLandmarkApiObject,
): StiffMeasure | null {
    const delta = subtractOrNull(
        tryComputeLumbarLordosisAtLevel(upright, L1),
        tryComputeLumbarLordosisAtLevel(seated, L1),
    );
    return delta === null ? null : { metric: 'deltaLumbarLordosis', delta };
}

/**
 * ΔSS stiff measure: sacral slope, standing − flexed-seated, or null when a
 * sacrum pair is missing.
 */
function sacralSlopeStiffMeasure(
    standing: HipSpineApprovedLandmarkApiObject,
    seated: HipSpineApprovedLandmarkApiObject,
): StiffMeasure | null {
    const delta = subtractOrNull(
        tryComputeSacralSlope(standing),
        tryComputeSacralSlope(seated),
    );
    return delta === null ? null : { metric: 'deltaSacralSlope', delta };
}

/**
 * Mirror of the backend `measured_at_l1` gate (hip_spine_angles.py): a posture
 * contributes an L1 lordosis measurement only when its approved endplate is L1,
 * its L1 endplate pair is present, AND its sacrum pair is present. The sacrum
 * check mirrors the backend's load-time guarantee (`from_records` requires the
 * sacrum, dropping a sacrum-less posture before selection), so such a source is
 * skipped — selection falls through to standing, else ΔSS — instead of being
 * chosen and degrading stiffness to null. When the field declares L1 but a
 * required pair is absent (e.g. determine-end-plate defaulting to L1 on
 * low-confidence data), mobility falls back to ΔSS.
 */
function measuredAtL1(landmarks: HipSpineApprovedLandmarkApiObject | null): boolean {
    return (
        landmarks !== null &&
        landmarks.lumbar_vertebra_end_plate === L1 &&
        hasLumbarEndplatePair(landmarks, L1) &&
        hasSacralEndplatePair(landmarks)
    );
}

/**
 * Choose the stiff-spine metric and compute its delta (upright − flexed-seated).
 *
 * ΔLL is used only when the flexed-seated posture is *measured at L1* AND at
 * least one upright posture (standing or lumbar-sagittal) is also measured at L1
 * — where "measured at L1" means the approved endplate is L1 AND the L1 endplate
 * pair is present (see {@link measuredAtL1}). When both upright candidates qualify,
 * the lumbar-sagittal posture is the upright source; otherwise whichever single
 * posture qualifies. Every other case — including any non-L1 endplate, or a
 * declared-L1 posture whose L1 pair is absent — falls back to ΔSS (sacral slope,
 * which needs only the sacrum pair). This pair-presence gate mirrors the backend
 * `select_mobility` exactly.
 *
 * Returns null when flexed-seated landmarks are absent, or when a landmark
 * required for the chosen delta is missing. Stiffness is an optional
 * dimension, so missing data degrades it to null rather than throwing.
 */
function selectStiffMeasure(inputs: HipSpineClassificationInputs): StiffMeasure | null {
    const { standingLandmarks: standing, flexedSeatedLandmarks: seated } = inputs;
    const lumbar = inputs.lumbarLandmarks;
    // No flexed-seated posture → stiffness can't be measured at all.
    if (!seated) return null;

    // Tie-break mirrors backend select_mobility: lumbar-sagittal supplies the
    // upright L1 state when measured at L1; else standing; else no L1 upright.
    const uprightL1 = measuredAtL1(lumbar)
        ? lumbar
        : measuredAtL1(standing)
          ? standing
          : null;

    return uprightL1 !== null && measuredAtL1(seated)
        ? lumbarLordosisStiffMeasure(uprightL1, seated)
        : sacralSlopeStiffMeasure(standing, seated);
}

/**
 * Compute the scalar measurements that drive classification.
 *
 * Throws {@link MissingLandmarksError} (from hipSpineAngleMeasurements) when any
 * *required* landmark is absent: pelvic incidence, standing lumbar lordosis (at
 * the standing posture's declared endplate), or APP angle. The viewmodel catches
 * this and renders an "incomplete landmarks" message.
 *
 * `stiff` is null (not a hard fail) when the flexed-seated posture is absent
 * or incomplete — stiffness is documented as an optional dimension.
 */
function measureHipSpine(inputs: HipSpineClassificationInputs): HipSpineMeasurements {
    const standing = inputs.standingLandmarks;
    const pelvicIncidence = computePelvicIncidence(standing);
    const lumbarLordosisStanding = computeLumbarLordosisAtLevel(
        standing,
        standing.lumbar_vertebra_end_plate,
    );
    const appAngle = computeAPPAngle(standing);

    return {
        pelvicIncidence,
        lumbarLordosisStanding,
        stiff: selectStiffMeasure(inputs),
        appAngle,
    };
}

/**
 * Apply clinical thresholds to scalar measurements. Pure: takes numbers,
 * returns booleans + recommendations. No landmarks, no I/O.
 */
export function classifyMeasurements(
    measurements: HipSpineMeasurements,
): HipSpineClassification {
    const pelvicIncidenceMinusLumbarLordosis =
        measurements.pelvicIncidence - measurements.lumbarLordosisStanding;
    const flatback = pelvicIncidenceMinusLumbarLordosis >= FLATBACK_THRESHOLD_DEGREES;
    const ppt = measurements.appAngle <= PPT_THRESHOLD;
    const base = {
        flatback,
        ppt,
        pelvicIncidenceMinusLumbarLordosis,
        appAngle: measurements.appAngle,
    };

    if (measurements.stiff === null) {
        return {
            ...base,
            stiff: null,
            recommendations: recommendationsFor({ flatback, stiff: null, ppt }),
        };
    }

    const stiffThresholdDegrees = STIFF_THRESHOLD_DEGREES[measurements.stiff.metric];
    const stiff = measurements.stiff.delta <= stiffThresholdDegrees;

    return {
        ...base,
        stiff,
        stiffDelta: measurements.stiff.delta,
        stiffMetricAbbreviation: STIFF_ABBREVIATION[measurements.stiff.metric],
        stiffThresholdDegrees,
        recommendations: recommendationsFor({ flatback, stiff, ppt }),
    };
}

export function classifyHipSpine(
    inputs: HipSpineClassificationInputs,
): HipSpineClassification {
    return classifyMeasurements(measureHipSpine(inputs));
}
